<template>
	<div id="top" class="site-shell wise-concept">
		<SiteHeader />
		<main id="main">
			<section class="wise-hero" aria-labelledby="wise-title">
				<div class="hero-masthead">
					<div class="hero-overline">
						<span>PAYDECK INDIA</span><span>EST. 2014</span>
					</div>
					<h1 id="wise-title">
						LIFE DOESN'T<br />HAPPEN AT A <em>CHECKOUT.</em>
					</h1>
					<div class="hero-deck">
						<p>
							Rent. Education. The people building your next
							chapter. Paydeck brings the payments behind real
							life into one place.
						</p>
						<div>
							<PdButton
								size="lg"
								href="#plan-a-payment"
								:icon-right="ArrowUpRight"
								>Start a payment</PdButton
							>
							<PdArrowLink
								href="#payments"
								arrow="right"
								tone="ink"
								>See all 11 ways to pay</PdArrowLink
							>
						</div>
					</div>
					<div class="hero-trust" role="group" aria-labelledby="hero-trust-title">
						<div class="hero-trust-copy">
							<p id="hero-trust-title" class="hero-trust-title">Trusted by 15.1 lakh+ users</p>
							<p>People across India pay rent, fees and deposits by card with Paydeck, through the banks and networks they already trust.</p>
						</div>
						<ul class="hero-trust-logos" aria-label="Card networks and payment partners">
							<li v-for="logo in heroLogos" :key="logo.name"><img :src="logo.src" :alt="logo.name" :class="`logo-${logo.key}`" loading="eager" /></li>
						</ul>
					</div>
				</div>
				<StoryStage />
			</section>

			<MotionRibbon :words="ribbonWords" label="Rent, education, deposits, services and more" />

			<section
				id="payments"
				class="wise-categories"
				aria-labelledby="categories-title"
			>
				<div class="payment-section-top" data-reveal>
					<div class="wise-section-head">
						<PdEyebrow tone="light"
							>A bigger world of payments</PdEyebrow
						>
						<h2 id="categories-title">
							WHAT'S ON<br /><em>YOUR LIST?</em>
						</h2>
					</div>
					<div class="payment-section-note">
						<strong>11</strong
						><span>real-life reasons<br />to make a payment.</span>
					</div>
				</div>
				<div
					class="wise-filter"
					role="group"
					aria-label="Filter payment categories"
				>
					<PdChip
						v-for="item in groups"
						:key="item"
						surface="dark"
						:selected="group === item"
						@click="chooseGroup(item)"
						>{{ item }}</PdChip
					>
				</div>
				<div class="payment-showcase">
					<div class="payment-spotlight" aria-live="polite">
						<img
							:key="selectedImage"
							:src="selectedImage"
							alt=""
							width="1122"
							height="1402"
						/>
						<div class="spotlight-shade" />
						<div class="spotlight-top">
							<span
								>PAYMENT PURPOSE /
								{{
									String(selectedIndex).padStart(2, "0")
								}}</span
							><span class="spotlight-orbit" aria-hidden="true"
								><component
									:is="selected.icon"
									:size="28"
									:stroke-width="1.7"
							/></span>
						</div>
						<div class="spotlight-copy" :key="selected.name">
							<span>SELECTED PURPOSE</span>
							<h3>{{ selected.name }}</h3>
							<p>{{ selected.detail }}</p>
							<a
								href="#plan-a-payment"
								@click="category = selected.name"
								>Plan this payment <ArrowUpRight :size="21"
							/></a>
						</div>
					</div>
					<div
						class="payment-directory"
						aria-label="Choose a payment purpose"
					>
						<button
							v-for="item in shown"
							:key="item.name"
							type="button"
							:class="[
								'directory-item',
								{ active: selected.name === item.name }
							]"
							:aria-pressed="selected.name === item.name"
							@click="category = item.name"
						>
							<span class="directory-number">{{
								String(categories.indexOf(item) + 1).padStart(
									2,
									"0"
								)
							}}</span>
							<span class="directory-icon"
								><component
									:is="item.icon"
									:size="22"
									:stroke-width="1.7"
							/></span>
							<span class="directory-copy"
								><strong>{{ item.name }}</strong
								><small>{{ item.detail }}</small></span
							>
							<ArrowUpRight
								:size="19"
								class="directory-arrow"
								aria-hidden="true"
							/>
						</button>
					</div>
				</div>
				<p class="wise-availability">
					Select a purpose to explore it. Payment availability and
					eligibility depend on the recipient and applicable terms.
				</p>
			</section>

			<section
				id="how-it-works"
				class="wise-how"
				aria-labelledby="how-title"
			>
				<div class="journey-heading" data-reveal>
					<PdEyebrow>The experience / In motion</PdEyebrow>
					<h2 id="how-title">FROM WHAT<br /><em>TO DONE.</em></h2>
					<p>
						A guided path from the reason for a payment to a clear
						record of it.
					</p>
				</div>
				<HowJourney :category="category" />
			</section>

			<MotionRibbon :words="trustWords" tone="ink" :icon="ShieldCheck" :icon-stroke="2.25" reverse label="Verified by your bank, transparent charges, registered with the Ministry of Corporate Affairs, and more" />

			<section id="why-paydeck" class="wise-confidence">
				<div class="confidence-title" data-reveal>
					<PdEyebrow>Why Paydeck / Backed by connections</PdEyebrow>
					<h2>
						Not new to<br />this.
						<em>Built for<br />what's next.</em>
					</h2>
					<p>Every payment on Paydeck runs on institutions you already know. Here is the track record, and the partners who stand behind every payment.</p>
				</div>
				<div class="confidence-stats" data-reveal>
					<PdTile
						v-for="(stat, i) in stats"
						:key="stat.label"
						:tone="stat.tone"
						:class="`stat-tile stat-tile-${i}`"
						:rings="i === 0"
					>
						<strong>{{ stat.value }}</strong>
						<span class="stat-copy">
							<span class="stat-label">{{ stat.label }}</span>
							<span v-if="stat.note" class="stat-note">{{ stat.note }}</span>
						</span>
					</PdTile>
				</div>
			</section>

			<TrustSections />

			<section
				class="wise-planner-section"
				aria-labelledby="planner-title"
			>
				<div class="planner-intro" data-reveal>
					<PdEyebrow>Your payment / Your purpose</PdEyebrow>
					<h2 id="planner-title">
						So, what's<br /><em>the plan?</em>
					</h2>
					<p>
						Start with a real payment purpose. See the journey
						clearly before you move forward.
					</p>
					<ol class="planner-steps">
						<li><b>1</b> Choose a payment</li>
						<li><b>2</b> Review the details</li>
						<li><b>3</b> Complete the guided flow</li>
					</ol>
				</div>
				<PaymentPlanner
					:category="category"
					@update:category="chooseCategory"
				/>
			</section>

			<section
				id="our-story"
				class="wise-story"
				aria-labelledby="story-title"
			>
				<div data-reveal>
					<PdEyebrow tone="light"
						>Started in India / Going global</PdEyebrow
					>
					<h2 id="story-title">
						<em>ONE IDEA.</em><br />NO BORDERS.
					</h2>
					<p>
						Paydeck started in 2014 when founder Saurabh Suman
						looked for a better way to pay rent and housing
						maintenance. Today the idea reaches across homes,
						education, services and projects.
					</p>
					<PdButton
						variant="primary"
						href="/about-us"
						:icon-right="ArrowUpRight"
						>Our story</PdButton
					>
				</div>
				<div class="story-next" data-reveal>
					<span>THE NEXT CHAPTER</span>
					<strong>INDIA <span class="story-route" aria-label="to"><ChevronsRight class="story-chevrons" :size="34" :stroke-width="2.5" aria-hidden="true" /><Plane class="story-plane" :size="34" :stroke-width="2" aria-hidden="true" /></span> THAILAND</strong>
					<p>
						More than a decade of experience is shaping Paydeck's
						next chapter in Thailand. Launching soon.
					</p>
					<PdArrowLink
						href="https://www.paydeck.in.th/en"
						tone="ink"
						target="_blank"
						rel="noopener noreferrer"
						>Explore Paydeck Thailand</PdArrowLink
					>
				</div>
			</section>
			<MotionRibbon v-if="showThailandRibbon" :words="thailandWords" tone="neon" :icon="Plane" :icon-stroke="2" label="Paydeck Thailand: every host verified, paid into a Thai bank, fees shown before you pay, launching soon in Bangkok, Phuket and Chiang Mai" />
		</main>
		<SiteFooter />
	</div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { ArrowUpRight, ChevronsRight, Plane, ShieldCheck } from "lucide-vue-next";
import MotionRibbon from "@/components/home/MotionRibbon.vue";
import SiteHeader from "@/components/site/SiteHeader.vue";
import SiteFooter from "@/components/site/SiteFooter.vue";
import StoryStage from "@/components/home/StoryStage.vue";
import PaymentPlanner from "@/components/home/PaymentPlanner.vue";
import HowJourney from "@/components/home/HowJourney.vue";
import TrustSections from "@/components/home/TrustSections.vue";
import PdButton from "@paydeck-ui/components/PdButton.vue";
import PdArrowLink from "@paydeck-ui/components/PdArrowLink.vue";
import PdEyebrow from "@paydeck-ui/components/PdEyebrow.vue";
import PdTile from "@paydeck-ui/components/PdTile.vue";
import PdChip from "@paydeck-ui/components/PdChip.vue";
import { categories, groups } from "@/content/home";

const stats = [
	{ value: "₹100Bn+", label: "Processed transactions", note: "Rent, fees, deposits and more, paid by card.", tone: "orange" },
	{ value: "12+", label: "Years of fintech service", tone: "ink" },
	{ value: "15.1L+", label: "Paydeck users", note: "People across India who pay with Paydeck.", tone: "white" },
	{ value: "1st", label: "India's first card based payment solution provider", tone: "peach" }
];

// Hero trust strip: real card networks and partners Paydeck payments run through (no customer brand logos)
const heroLogos = [
	{ key: "visa", name: "Visa", src: "/images/partners/visa.svg" },
	{ key: "mastercard", name: "Mastercard", src: "/images/partners/mastercard.svg" },
	{ key: "rupay", name: "RuPay", src: "/images/partners/rupay.png" },
	{ key: "hdfc", name: "HDFC Bank", src: "/images/partners/hdfc.svg" },
	{ key: "razorpay", name: "Razorpay", src: "/images/partners/razorpay.svg" }
];
const ribbonWords = ["RENT", "EDUCATION", "DEPOSITS", "SERVICES", "SUPPLIERS", "REAL LIFE"];
// Trust signals that hold for Paydeck today: no certification claims the site cannot back up
// Neon Thailand ribbon above the footer: hidden for now, set to true to show it again
const showThailandRibbon = false;
// Paydeck Thailand's own wording, from paydeck.in.th
const thailandWords = ["PAYDECK THAILAND", "RENT BY CARD, IN BAHT", "EVERY HOST VERIFIED", "PAID INTO A THAI BANK", "FEES SHOWN BEFORE YOU PAY", "DEPOSITS WITH PROOF", "BILLS AND UTILITIES", "BANGKOK · PHUKET · CHIANG MAI", "LAUNCHING SOON"];
const trustWords = ["VERIFIED BY YOUR BANK", "TRANSPARENT CHARGES", "VISA, MASTERCARD AND RUPAY", "PAID STRAIGHT TO BANK ACCOUNTS", "A RECEIPT FOR EVERY PAYMENT", "TRUSTED SINCE 2014", "MADE IN INDIA"];
const group = ref("All payments");
const category = ref("Rent");
const shown = computed(() =>
	group.value === "All payments"
		? categories
		: categories.filter((item) => item.group === group.value)
);
const selected = computed(
	() =>
		categories.find((item) => item.name === category.value) || categories[1]
);
const selectedIndex = computed(() => categories.indexOf(selected.value) + 1);
const selectedImage = computed(() => {
	if (selected.value.name === "Education")
		return "/images/education-story.webp";
	return selected.value.group === "Home & property"
		? "/images/rent-story.webp"
		: "/images/service-story.webp";
});

function chooseCategory(name) {
	category.value = name;
	const match = categories.find((item) => item.name === name);
	if (group.value !== "All payments" && match && match.group !== group.value)
		group.value = "All payments";
}

function chooseGroup(item) {
	group.value = item;
	if (item === "All payments") return;
	const first = categories.find((entry) => entry.group === item);
	if (first) category.value = first.name;
}

let observer = null;
onMounted(() => {
	if (
		!("IntersectionObserver" in window) ||
		window.matchMedia("(prefers-reduced-motion: reduce)").matches
	)
		return;
	observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					entry.target.classList.add("is-visible");
					observer.unobserve(entry.target);
				}
			});
		},
		{ threshold: 0.08, rootMargin: "0px 0px -35px 0px" }
	);
	document
		.querySelectorAll("[data-reveal]")
		.forEach((element) => observer.observe(element));
	const root = document.querySelector(".wise-concept");
	if (root) root.classList.add("motion-ready");
});
onBeforeUnmount(() => {
	if (observer) observer.disconnect();
});
</script>
